<template>
  <card-dialog
    v-model="modalVisible"
    :title="$t('card_dialog.match_preparation.title')"
    max-width="1000px"
    prevent-close
    @close="closeConfirmationDialogVisible = true"
  >
    <v-card-text class="pt-6">
      <v-row>
        <v-col>
          <v-data-table
            :items="event.players"
            :headers="headers"
            item-key="id"
            class="elevation-0"
            dense
            sort-by="jersey_number"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.jersey_number="{ item }">
              {{ item.jersey_number ? item.jersey_number : '-' }}
            </template>
            <template v-slot:item.last_name="{ item }">
              <div style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px;">
                {{ item.last_name }}, {{ item.first_name }}
              </div>
            </template>

            <template v-slot:item.roles="{ item }">
              <v-row
                no-gutters
                align="center"
                style="white-space: nowrap;"
              >
                <v-col cols="auto">
                  <v-tooltip
                    v-for="role in playerRoles.data"
                    :key="role.id"
                    top
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        class="mx-1 px-1"
                        small
                        :class="item.player_match_role && item.player_match_role.id === role.id ? 'text-none' : 'text-none v-btn--outlined'"
                        :color="item.player_match_role && item.player_match_role.id === role.id ? 'primary' : undefined"
                        :text="!item.player_match_role || item.player_match_role.id !== role.id"
                        @click="item.player_match_role = role"
                        v-on="on"
                      >
                        {{ role.short_name }}
                      </v-btn>
                    </template>
                    {{ role.name }}
                  </v-tooltip>
                </v-col>
              </v-row>
            </template>
          </v-data-table>
        </v-col>
        <v-col>
          <v-row no-gutters>
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100 font-weight-medium">
                {{ $t("admin.staff") }}
              </div>
            </v-col>
          </v-row>
          <v-row class="d-flex flex-column">
            <v-col
              v-for="coach in event.coaches"
              :key="coach.id"
              class="py-2"
            >
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  <v-list-item-title style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px;">
                    {{ coach.first_name }} {{ coach.last_name }}
                  </v-list-item-title>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="coach.coach_match_role"
                    dense
                    outlined
                    clearable
                    hide-details
                    :items="coachRoles.data"
                    value="name"
                    item-text="name"
                    :label="$t('dropdown.coach_role.title')"
                    return-object
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="mt-6">
            <v-col>
              <div class="subtitle-2 grey--text text--grey-100 font-weight-medium">
                {{ $t("card_title.special_roles") }}
              </div>
            </v-col>
          </v-row>
          <v-row class="d-flex flex-column">
            <v-col
              class="py-2"
            >
              <v-row
                no-gutters
                align="center"
              >
                <v-col>
                  <v-list-item-title>{{ $t("dropdown.captain.title") }}</v-list-item-title>
                </v-col>
                <v-col cols="6">
                  <v-select
                    v-model="captain"
                    dense
                    outlined
                    clearable
                    hide-details
                    :items="event.players"
                    value="last_name"
                    item-text="last_name"
                    :label="$t('dropdown.players.title')"
                    return-object
                  >
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ item.first_name }} {{ item.last_name }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <span v-if="item.player_positions && item.player_positions.length > 0">{{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </template>
                    <template v-slot:selection="{ item }">
                      {{ item.first_name }} {{ item.last_name }}
                    </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-card-text>
    <template v-slot:actions>
      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn
          color="primary"
          class="text-none"
          :loading="submitting"
          @click="saveMatchPreparation()"
        >
          {{ $t("button.save") }}
        </v-btn>
      </v-card-actions>
    </template>

    <delete-confirmation-dialog
      :visible.sync="closeConfirmationDialogVisible"
      :title="$t('card_dialog.lose_progress.title')"
      :text="$t('card_dialog.lose_progress.label')"
      :cta-text="$t('card_dialog.lose_progress.button')"
      @confirmed="modalVisible = false"
    />
  </card-dialog>
</template>

<script>

import CardDialog from '@/components/helpers/CardDialog.vue'
import { EventApiService } from '@/data/services'
import { MatchRolesApiService } from '@/data/services/MatchRolesApiService'
import DeleteConfirmationDialog from '@/components/dialogs/DeleteConfirmationDialog.vue'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import { toCamelCase } from '@/utils/helpers'
import { EventBus } from '@/plugins/bus'
import { cloneDeep } from 'lodash'

export default {
  name: 'MatchPrepareDialog',
  components: { DeleteConfirmationDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    eventDetails: {
      type: Object,
      required: true
    }
  },
  data: () => ({
    event: {},
    playerRoles: {
      data: null,
      loading: false
    },
    coachRoles: {
      data: null,
      loading: false
    },
    submitting: false,
    captain: null,
    closeConfirmationDialogVisible: false
  }),
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    headers() {
      return [
        { text: '#', value: 'jersey_number', width: '1%' },
        { text: this.$t('table_title.players'), value: 'last_name' },
        { text: this.$t('table_headers.match_preparation.roles'), value: 'roles', width: '1%', sortable: false }
      ]
    }
  },
  watch: {
    visible(newValue) {
      if (newValue) {
        this.event = cloneDeep(this.eventDetails)
        this.captain = this.event.players.find(player => player.is_captain)
      }
    }
  },
  created() {
    this.fetchPlayerRoles()
    this.fetchCoachRoles()
  },
  methods: {
    async fetchPlayerRoles() {
      this.playerRoles.loading = true

      try {
        const response = await MatchRolesApiService.getPlayerRoles()

        this.playerRoles.data = response.data.map(role => {
          return {
            ...role,
            name: this.$t(`player_match_roles.${role.short_name}`),
            short_name: this.$t(`player_match_roles.${role.short_name}.short`)
          }
        })
      } catch (e) {
        this.playerRoles.data = []
        throw e
      } finally {
        this.playerRoles.loading = false
      }
    },
    async fetchCoachRoles() {
      this.coachRoles.loading = true

      try {
        const response = await MatchRolesApiService.getCoachRoles()

        this.coachRoles.data = response.data.map(role => {
          return {
            ...role,
            name: this.$t(`coach_match_roles.${toCamelCase(role.name)}`)
          }
        })
      } catch (e) {
        this.coachRoles.data = []
        throw e
      } finally {
        this.coachRoles.loading = false
      }
    },
    async saveMatchPreparation() {
      try {
        this.submitting = true

        const data = {
          players: this.event.players.map(player => {
            return {
              id: player.id,
              is_captain: this.captain?.id === player.id,
              player_match_role_id: player.player_match_role?.id ?? null
            }
          }),
          coaches: this.event.coaches.map(coach => {
            return {
              id: coach.id,
              coach_match_role_id: coach.coach_match_role?.id ?? null
            }
          })
        }

        await EventApiService.saveMatchPreparation(this.event.id, data)

        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_preparation_save.success'),
          color: 'success'
        })

        EventBus.$emit(this.$events.EVENT_UPDATED, this.event)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.match_preparation_save.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.submitting = false
        this.modalVisible = false
      }
    }
  }
}
</script>

<style scoped>

</style>
